import { Link, NavLink, useNavigate } from 'react-router-dom'
import { Logo } from './Logo'
import { Avatar } from './Avatar'
import { useAuth } from '@/context/AuthContext'
import { ORG_MANAGER_ROLES } from '@/domain/roles'

const linkClass = ({ isActive }: { isActive: boolean }) =>
  `text-sm font-medium transition ${isActive ? 'text-teal-300' : 'text-white/70 hover:text-white'}`

export function Navbar() {
  const { currentUser, logout } = useAuth()
  const navigate = useNavigate()
  const isOrgManager = !!currentUser && ORG_MANAGER_ROLES.includes(currentUser.role)
  // Leaderboard only makes sense for org-affiliated learners/managers —
  // Individual Learners have no cohort to rank against (Document 11 §5).
  const showLeaderboard = !!currentUser && (currentUser.orgId !== null || currentUser.role === 'super_admin')

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  return (
    <header className="sticky top-0 z-40 border-b border-white/10 bg-navy-950/90 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to={currentUser ? '/dashboard' : '/'}>
          <Logo size={48} />
        </Link>

        {currentUser && (
          <nav className="hidden items-center gap-6 md:flex">
            <NavLink to="/dashboard" className={linkClass}>
              Dashboard
            </NavLink>
            <NavLink to="/episodes" className={linkClass}>
              Episode Library
            </NavLink>
            <NavLink to="/missions" className={linkClass}>
              Missions
            </NavLink>
            {showLeaderboard && (
              <NavLink to="/leaderboard" className={linkClass}>
                Leaderboard
              </NavLink>
            )}
            {isOrgManager && (
              <NavLink to="/team" className={linkClass}>
                {currentUser.role === 'teacher' ? 'My Class' : 'Team'}
              </NavLink>
            )}
            <NavLink to="/profile" className={linkClass}>
              Profile
            </NavLink>
          </nav>
        )}

        <div className="flex items-center gap-3">
          {currentUser ? (
            <>
              <div className="hidden text-right sm:block">
                <p className="text-sm font-medium text-white">{currentUser.displayName}</p>
              </div>
              <Avatar name={currentUser.displayName} color={currentUser.avatarColor} size={36} />
              <button onClick={handleLogout} className="cyber-btn-secondary !px-3 !py-1.5 text-xs">
                Log out
              </button>
            </>
          ) : (
            <Link to="/login" className="cyber-btn-primary !px-4 !py-2 text-sm">
              Demo Login
            </Link>
          )}
        </div>
      </div>

      {currentUser && (
        <nav className="flex items-center gap-4 overflow-x-auto border-t border-white/5 px-4 py-2 md:hidden">
          <NavLink to="/dashboard" className={linkClass}>
            Dashboard
          </NavLink>
          <NavLink to="/episodes" className={linkClass}>
            Episodes
          </NavLink>
          <NavLink to="/missions" className={linkClass}>
            Missions
          </NavLink>
          {showLeaderboard && (
            <NavLink to="/leaderboard" className={linkClass}>
              Leaderboard
            </NavLink>
          )}
          {isOrgManager && (
            <NavLink to="/team" className={linkClass}>
              {currentUser.role === 'teacher' ? 'My Class' : 'Team'}
            </NavLink>
          )}
          <NavLink to="/profile" className={linkClass}>
            Profile
          </NavLink>
        </nav>
      )}
    </header>
  )
}
